import { DailyCall } from '@daily-co/daily-js';

/**
 * Low-network / high-CPU / join-failure telemetry for one participant-class.
 *
 * Why this exists: a failed join and a bad connection currently leave no trace
 * anywhere. Of 106 "can't join class" tickets from 1-13 Oct 2026, 17 had a
 * student with a registration and a joining_count up to 12 and no participant
 * row at all -- the only evidence of the failure was an absence. This module
 * writes the evidence.
 *
 * Note the path segment is `v1s`, not `v1`, matching the four siblings this
 * app already calls (class_time, room_presence, room_presence_redis,
 * create_room_screenshot).
 */
const GALAXY_PATH = '/api/v1s/classroom/prebuilt/quality_events';

/**
 * Why this one endpoint reads a base URL while the four siblings
 * (class_time, room_presence, room_presence_redis, create_room_screenshot)
 * hardcode `https://www.planetspark.in`: there is no shared galaxy base in
 * this app to read from -- all four spell the absolute production URL out
 * inline. Telemetry is the one call where that is actively wrong, because a
 * staging build would silently write rows into the production table and make
 * them indistinguishable from real classes. The default keeps production
 * behaviour identical to the siblings, and the override follows the
 * `NEXT_PUBLIC_* || default` idiom already used for the call-machine bundle in
 * `contexts/useCallMachine.ts`. Giving all five a shared base is a separate
 * change.
 */
export const QUALITY_EVENTS_URL = `${(
  process.env.NEXT_PUBLIC_GALAXY_BASE_URL || 'https://www.planetspark.in'
).replace(/\/+$/, '')}${GALAXY_PATH}`;

/**
 * Per page load, not per request. ~2,000 participant-classes a day go through
 * here, so the cap is what keeps the table affordable: a participant on a
 * link that flaps for an hour writes 40 rows, not 400.
 */
export const MAX_EVENTS_PER_PAGE_LOAD = 40;
/** Wire limit per request. Below the page-load cap, so it never actually bites. */
export const MAX_EVENTS_PER_REQUEST = 50;
/**
 * A flapping link cannot emit more than one event per kind per this window.
 * Daily re-fires `network-quality-change` on every measurement, so without
 * this a single bad minute would be dozens of rows saying the same thing.
 */
export const EVENT_DEBOUNCE_MS = 10_000;
export const FLUSH_INTERVAL_MS = 20_000;
/**
 * How long after a join attempt we wait for `joined-meeting` before calling it
 * a failure. The join promise rejecting is the easy case; the case the tickets
 * are full of is a join that neither resolves nor rejects.
 */
export const JOIN_TIMEOUT_MS = 20_000;

export type QualityEventKind =
  | 'network_poor'
  | 'network_interrupted'
  | 'network_restored'
  | 'cpu_high'
  | 'join_failed';

/**
 * No `payload.source`, deliberately and load-bearingly.
 *
 * On a Daily class galaxy's own `DailyIframe.wrap` listeners watch the same
 * participant as this reporter, resolve the same slot and user, and are
 * equally correct -- so every interruption and every second of poor network
 * would be counted twice, and since the two clocks differ by milliseconds no
 * dedupe index can collapse the pair. Galaxy settles it by provenance: its
 * page listeners stamp `payload.source = 'page'`, and a row arriving with no
 * `source` is taken to be the in-call reporter, which the rollup prefers per
 * (slot, user, platform) because it owns the real call object and sees the
 * richer stats.
 *
 * Sending a `source` of our own would therefore demote this client and
 * resurrect the double count. The silence is the signal. (Galaxy side:
 * 9779611cb4.)
 */
export interface QualityEvent {
  kind: QualityEventKind;
  severity: 1 | 2;
  /** ISO-8601 UTC. */
  occurred_at: string;
  /** Whole seconds a state lasted, set when the state ends. */
  duration_seconds?: number;
  payload: Record<string, unknown>;
}

export interface RoomQualityReporterConfig {
  roomName?: string;
  userId?: string;
}

/** Phase a `join_failed` event was detected in. */
export type JoinFailurePhase = 'join_rejected' | 'join_timeout';

/**
 * Reads the class occurrence id from this app's own URL.
 *
 * Deliberately NOT the Daily room name: LiveKit and Daily room names are
 * reused every week, so a row keyed by room name cannot name the class it
 * belongs to. Galaxy appends `event_time_slot_id` (and `user_id`) to the room
 * URL for exactly this. If it is absent we send nothing at all -- a row that
 * cannot name its class is useless, and writing it would make the table look
 * populated while answering no question.
 */
export const resolveEventTimeSlotId = (search?: string): number | undefined => {
  try {
    const raw =
      typeof search === 'string'
        ? search
        : typeof window === 'undefined'
        ? ''
        : window.location.search;
    const value = new URLSearchParams(raw).get('event_time_slot_id');
    if (!value) return undefined;
    const parsed = Number.parseInt(value, 10);
    return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : undefined;
  } catch {
    return undefined;
  }
};

const newClientSessionId = () => {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function')
      return crypto.randomUUID();
  } catch {
    // fall through
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 14)}`;
};

/** Daily error objects carry the vendor detail under `error`, not `message`. */
const describeJoinError = (error: unknown) => {
  const err = (error ?? {}) as Record<string, any>;
  const message =
    err.errorMsg ?? err.message ?? (typeof error === 'string' ? error : '');
  const vendorError = err.error?.type ?? err.type ?? err.error?.msg ?? null;
  return {
    message: String(message ?? '').slice(0, 300),
    vendor_error: vendorError === null ? null : String(vendorError).slice(0, 120),
  };
};

export interface RoomQualityReporter {
  configure(config: RoomQualityReporterConfig): void;
  attach(call: DailyCall): () => void;
  noteJoinAttempt(): void;
  noteJoinSucceeded(): void;
  noteJoinFailure(error: unknown, phase?: JoinFailurePhase): void;
  flush(): void;
  stop(): void;
  /** Test seam only. */
  readonly buffered: QualityEvent[];
}

const createRoomQualityReporter = (): RoomQualityReporter => {
  const clientSessionId = newClientSessionId();
  let roomName: string | undefined;
  let userId: string | undefined;

  let buffer: QualityEvent[] = [];
  /** Counts every event ever buffered, so the cap survives a flush. */
  let emitted = 0;
  let truncated = false;
  let truncatedReported = false;
  let slotIdMissingLogged = false;
  let networkStatsRead = false;

  const lastEmittedAt = new Map<QualityEventKind, number>();
  /**
   * The buffered event a still-open state started with, so that when the
   * state ends we can set its `duration_seconds` in place instead of writing
   * a second row. Once the event has been flushed there is nothing to patch
   * and the duration is simply absent -- cheaper than a row per edge.
   */
  const openStates = new Map<
    'network_poor' | 'network_interrupted' | 'cpu_high',
    { startedAt: number; event: QualityEvent }
  >();

  let lastThreshold: string | undefined;
  let lastConnection: string | undefined;
  let lastCpuState: string | undefined;

  let flushTimer: ReturnType<typeof setInterval> | undefined;
  let joinTimer: ReturnType<typeof setTimeout> | undefined;
  let joinSettled = false;
  let detachCall: (() => void) | undefined;
  let detachPage: (() => void) | undefined;
  let detachAll: (() => void) | undefined;
  let attachedCall: DailyCall | undefined;

  const configure = (config: RoomQualityReporterConfig) => {
    if (config.roomName) roomName = config.roomName;
    if (config.userId) userId = config.userId;
  };

  const emit = (
    kind: QualityEventKind,
    severity: 1 | 2,
    payload: Record<string, unknown>
  ): QualityEvent | undefined => {
    const now = Date.now();

    const previous = lastEmittedAt.get(kind);
    if (previous !== undefined && now - previous < EVENT_DEBOUNCE_MS) return;

    if (emitted >= MAX_EVENTS_PER_PAGE_LOAD) {
      // Stop buffering, but tell galaxy the series it is looking at is cut off,
      // so a quiet tail is not read as a recovery.
      truncated = true;
      return;
    }

    const event: QualityEvent = {
      kind,
      severity,
      occurred_at: new Date(now).toISOString(),
      payload,
    };
    buffer.push(event);
    emitted += 1;
    lastEmittedAt.set(kind, now);
    return event;
  };

  const openState = (
    state: 'network_poor' | 'network_interrupted' | 'cpu_high',
    event: QualityEvent | undefined
  ) => {
    if (!event) return;
    openStates.set(state, { startedAt: Date.now(), event });
  };

  const closeState = (
    state: 'network_poor' | 'network_interrupted' | 'cpu_high'
  ) => {
    const open = openStates.get(state);
    openStates.delete(state);
    if (!open) return undefined;
    const seconds = Math.max(0, Math.round((Date.now() - open.startedAt) / 1000));
    if (buffer.includes(open.event)) open.event.duration_seconds = seconds;
    return seconds;
  };

  /**
   * One `getNetworkStats()` read per page load, attached to the first
   * `network_poor` event if it is still buffered. Never per event: it is a
   * real RTCPeerConnection stats read and this code must not cost the call
   * anything measurable.
   */
  const enrichWithNetworkStats = (call: DailyCall, event: QualityEvent) => {
    if (networkStatsRead) return;
    networkStatsRead = true;
    try {
      Promise.resolve(call.getNetworkStats())
        .then((stats: any) => {
          try {
            if (!buffer.includes(event)) return;
            const latest = stats?.latest ?? {};
            event.payload = {
              ...event.payload,
              recv_packet_loss: latest.recvPacketLoss ?? null,
              send_packet_loss: latest.sendPacketLoss ?? null,
              video_recv_bitrate: latest.videoRecvBitsPerSecond ?? null,
              video_send_bitrate: latest.videoSendBitsPerSecond ?? null,
            };
          } catch {
            // Enrichment is a nicety; the event already stands on its own.
          }
        })
        .catch(() => {});
    } catch {
      // ignore
    }
  };

  /**
   * A `FormData`, exactly like this app's four sibling galaxy calls
   * (class_time, room_presence, room_presence_redis, create_room_screenshot).
   *
   * That is not just for consistency: `navigator.sendBeacon` accepts a
   * FormData and sends `multipart/form-data`, which is one of the three
   * CORS-safelisted content types, so no preflight is involved at all. A Blob
   * of `application/json` would be the opposite -- a beacon cannot run a
   * preflight, so the browser discards every cross-origin flush with no error
   * and no network entry. The symptom is the worst possible one for this
   * feature: it works on localhost (same origin) and nothing ever arrives in
   * production, on exactly the unload path this telemetry exists to capture.
   *
   * `events` therefore travels as a JSON string in a form field, which galaxy
   * parses back on arrival. Everything else is the same scalars the siblings
   * already send.
   */
  const buildBody = (events: QualityEvent[]) => {
    const form = new FormData();
    form.append('api_token', 'Dy2MwYGdmDxxB9PZQE7yyfVBpWix7zWMrpgX');
    form.append('app_key', 'DailyCoPrebuilt');
    form.append('app_version', '1.0');
    form.append('meeting_platform', 'dailyco');
    form.append('room_name', roomName ?? '');
    form.append('event_time_slot_id', String(resolveEventTimeSlotId()));
    form.append('user_id', userId ?? '');
    form.append('client_session_id', clientSessionId);
    if (truncated) form.append('truncated', 'true');
    form.append('events', JSON.stringify(events));
    return form;
  };

  const send = (events: QualityEvent[]) => {
    const body = buildBody(events);
    // A beacon, not fetch: the sessions this telemetry is for are the ones
    // that die mid-page, and a fetch is cancelled on unload.
    if (
      typeof navigator !== 'undefined' &&
      typeof navigator.sendBeacon === 'function'
    ) {
      if (navigator.sendBeacon(QUALITY_EVENTS_URL, body)) return;
    }
    // Only as a fallback: a browser without sendBeacon at all. No explicit
    // Content-Type -- fetch sets it with the multipart boundary.
    fetch(QUALITY_EVENTS_URL, {
      method: 'POST',
      body,
      keepalive: true,
    }).catch(() => {});
  };

  const flush = () => {
    try {
      /**
       * A capped reporter buffers nothing, so without this there would be no
       * "next request" left to carry the flag and a cut-off series would look
       * like a participant whose connection quietly recovered. One empty body
       * per page load at most.
       */
      const owesTruncatedFlag = truncated && !truncatedReported;
      if (!buffer.length && !owesTruncatedFlag) return;
      // No slot id means no class to attribute the rows to, so there is
      // nothing worth sending. Logged once, then silent.
      if (resolveEventTimeSlotId() === undefined) {
        if (!slotIdMissingLogged) {
          slotIdMissingLogged = true;
          console.debug(
            '[roomQuality] no event_time_slot_id on the URL; not reporting'
          );
        }
        buffer = [];
        return;
      }
      if (!roomName) return;

      const pending = buffer;
      buffer = [];
      if (truncated) truncatedReported = true;
      if (!pending.length) {
        send([]);
        return;
      }
      for (let i = 0; i < pending.length; i += MAX_EVENTS_PER_REQUEST) {
        send(pending.slice(i, i + MAX_EVENTS_PER_REQUEST));
      }
    } catch {
      // Telemetry must never affect the call.
    }
  };

  const clearJoinTimer = () => {
    if (joinTimer !== undefined) {
      clearTimeout(joinTimer);
      joinTimer = undefined;
    }
  };

  const noteJoinAttempt = () => {
    try {
      if (joinSettled) return;
      clearJoinTimer();
      joinTimer = setTimeout(() => {
        joinTimer = undefined;
        if (joinSettled) return;
        joinSettled = true;
        emit('join_failed', 2, {
          message: `no local media after ${JOIN_TIMEOUT_MS / 1000}s`,
          vendor_error: null,
          phase: 'join_timeout' as JoinFailurePhase,
        });
        flush();
      }, JOIN_TIMEOUT_MS);
    } catch {
      // ignore
    }
  };

  const noteJoinSucceeded = () => {
    try {
      joinSettled = true;
      clearJoinTimer();
    } catch {
      // ignore
    }
  };

  const noteJoinFailure = (
    error: unknown,
    phase: JoinFailurePhase = 'join_rejected'
  ) => {
    try {
      joinSettled = true;
      clearJoinTimer();
      emit('join_failed', 2, { ...describeJoinError(error), phase });
      flush();
    } catch {
      // ignore
    }
  };

  /**
   * Every handler body is wrapped: a throw in here would otherwise travel out
   * through daily-js's emitter and take the call's own listeners with it.
   */
  const guard =
    <T>(handler: (ev: T) => void) =>
    (ev: T) => {
      try {
        handler(ev);
      } catch {
        // Telemetry must never break the call.
      }
    };

  const attach = (call: DailyCall) => {
    /**
     * Attached twice for one call object on purpose: once synchronously where
     * the call object is created, so the listeners exist before `join()` is
     * ever called, and once from the effect that owns the detach. A join that
     * fails is the most valuable event in the set, and a listener registered
     * after the join has started never sees it. The second attach is a no-op.
     */
    if (attachedCall === call && detachAll) return detachAll;
    attachedCall = call;

    const onNetworkQuality = guard((ev: any) => {
      const threshold = ev?.threshold;
      // Transitions only. Daily re-fires this on every measurement.
      if (threshold === lastThreshold) return;
      const wasPoor = lastThreshold === 'low' || lastThreshold === 'very-low';
      lastThreshold = threshold;

      if (threshold === 'low' || threshold === 'very-low') {
        const event = emit('network_poor', threshold === 'very-low' ? 2 : 1, {
          threshold,
          quality: ev?.quality ?? null,
        });
        if (!wasPoor) openState('network_poor', event);
        if (event) enrichWithNetworkStats(call, event);
        return;
      }
      if (wasPoor) closeState('network_poor');
    });

    const onNetworkConnection = guard((ev: any) => {
      const event = ev?.event;
      if (event === lastConnection) return;
      lastConnection = event;

      if (event === 'interrupted') {
        openState(
          'network_interrupted',
          emit('network_interrupted', 2, { type: ev?.type ?? null })
        );
        return;
      }
      if (event === 'connected') {
        const seconds = closeState('network_interrupted');
        const restored = emit('network_restored', 2, { type: ev?.type ?? null });
        // The restore event is the one that knows how long the outage lasted,
        // and unlike the interruption it travels over a network that works.
        if (restored && seconds !== undefined) restored.duration_seconds = seconds;
      }
    });

    const onCpuLoad = guard((ev: any) => {
      const state = ev?.cpuLoadState;
      if (state === lastCpuState) return;
      const wasHigh = lastCpuState === 'high';
      lastCpuState = state;

      if (state === 'high') {
        const event = emit('cpu_high', 1, {
          reason: ev?.cpuLoadStateReason ?? null,
        });
        if (!wasHigh) openState('cpu_high', event);
        return;
      }
      if (wasHigh) closeState('cpu_high');
    });

    const onJoined = guard(() => noteJoinSucceeded());
    const onError = guard((ev: any) => noteJoinFailure(ev, 'join_rejected'));

    try {
      call.on('network-quality-change', onNetworkQuality);
      call.on('network-connection', onNetworkConnection);
      call.on('cpu-load-change', onCpuLoad);
      call.on('joined-meeting', onJoined);
      call.on('error', onError);
    } catch {
      // A call object that refuses listeners is not a reason to fail a join.
    }

    const onHidden = guard(() => {
      if (document.visibilityState === 'hidden') flush();
    });
    const onPageHide = guard(() => flush());

    try {
      if (typeof document !== 'undefined')
        document.addEventListener('visibilitychange', onHidden);
      if (typeof window !== 'undefined')
        window.addEventListener('pagehide', onPageHide);
    } catch {
      // ignore
    }

    if (flushTimer === undefined) {
      flushTimer = setInterval(() => flush(), FLUSH_INTERVAL_MS);
    }

    detachPage = () => {
      try {
        if (typeof document !== 'undefined')
          document.removeEventListener('visibilitychange', onHidden);
        if (typeof window !== 'undefined')
          window.removeEventListener('pagehide', onPageHide);
      } catch {
        // ignore
      }
    };

    detachCall = () => {
      try {
        call.off('network-quality-change', onNetworkQuality);
        call.off('network-connection', onNetworkConnection);
        call.off('cpu-load-change', onCpuLoad);
        call.off('joined-meeting', onJoined);
        call.off('error', onError);
      } catch {
        // ignore
      }
    };

    detachAll = () => {
      detachCall?.();
      detachCall = undefined;
      detachPage?.();
      detachPage = undefined;
      // Cleared so a later attach of the same call object registers again,
      // rather than handing back a detach that has already run.
      attachedCall = undefined;
      detachAll = undefined;
    };
    return detachAll;
  };

  const stop = () => {
    try {
      detachAll?.();
      clearJoinTimer();
      if (flushTimer !== undefined) {
        clearInterval(flushTimer);
        flushTimer = undefined;
      }
      flush();
    } catch {
      // ignore
    }
  };

  return {
    attach,
    configure,
    flush,
    noteJoinAttempt,
    noteJoinFailure,
    noteJoinSucceeded,
    stop,
    get buffered() {
      return buffer;
    },
  };
};

let singleton: RoomQualityReporter | undefined;

/**
 * One reporter per page load: `client_session_id` must be stable for the tab
 * and the 40-event cap is per page load, so neither can live in a React
 * instance that a remount would replace.
 */
export const getRoomQualityReporter = (): RoomQualityReporter => {
  if (!singleton) singleton = createRoomQualityReporter();
  return singleton;
};

/** Test seam: drops the page-load singleton. */
export const resetRoomQualityReporter = () => {
  singleton = undefined;
};
